import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, ShoppingBag, Clock, User as UserIcon, Edit3, Lock, Mail, Phone, CheckCircle, AlertCircle, MessageSquare, X, Send, CalendarCheck, Upload, Ticket, Trophy, Users, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { NotificationSettings } from '../components/NotificationSettings';
import { ReservationCard } from '../components/ReservationSystem';
import { ReferralSection } from '../components/ReferralSection';
import { GamificationPanel } from '../components/GamificationPanel';
import { UserCounter } from '../components/UserCounter';
import { TicketUploader } from '../components/TicketUploader';
import { DataPrivacySection } from '../components/DataPrivacySection';
import { InlineAdBanner } from '../components/AdComponents';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProfilePage = () => {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || 'null'));
  const [purchases, setPurchases] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showPhoneVerifyModal, setShowPhoneVerifyModal] = useState(false);
  const [activeTab, setActiveTab] = useState('reservations');
  const [ticketUploadReservation, setTicketUploadReservation] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    surname: '',
    email: '',
    phone: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [supportForm, setSupportForm] = useState({
    subject: '',
    message: ''
  });
  const [phoneVerifyForm, setPhoneVerifyForm] = useState({
    phone: '',
    code: '',
    step: 'phone', // 'phone' | 'code'
    codeSent: false,
    mockMode: false
  });
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    fetchPurchases();
    fetchReservations();
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`, {
        headers: { 'X-User-Id': user.id }
      });
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      setEditForm({
        name: response.data.name || '',
        surname: response.data.surname || '',
        email: response.data.email || '',
        phone: response.data.phone || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchReservations = async () => {
    try {
      const response = await axios.get(`${API}/reservations/user`, {
        headers: { 'X-User-Id': user.id }
      });
      setReservations(response.data);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    }
  };

  const fetchPurchases = async () => {
    try {
      const response = await axios.get(`${API}/purchases/user/${user.id}`);
      setPurchases(response.data);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/profile`, editForm, {
        headers: { 'X-User-Id': user.id }
      });
      toast.success('Perfil actualizado');
      setEditMode(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error actualizando perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/profile/change-password`, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      }, {
        headers: { 'X-User-Id': user.id }
      });
      toast.success('Contraseña actualizada');
      setShowPasswordModal(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error cambiando contraseña');
    } finally {
      setSaving(false);
    }
  };

  const handleSendSupport = async () => {
    if (!supportForm.subject || !supportForm.message) {
      toast.error('Completa todos los campos');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/support`, supportForm, {
        headers: { 'X-User-Id': user.id }
      });
      toast.success('Mensaje enviado. Te responderemos pronto.');
      setShowSupportModal(false);
      setSupportForm({ subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error enviando mensaje');
    } finally {
      setSaving(false);
    }
  };

  const sendEmailVerification = async () => {
    setVerifying(true);
    try {
      const response = await axios.post(`${API}/verification/send-email`, {}, {
        headers: { 'X-User-Id': user.id }
      });
      if (response.data.mock) {
        toast.success(t('verification.email_mock') || 'Email simulado (modo prueba). Revisa la consola para el token.');
        console.log('Mock verification token:', response.data.token);
      } else {
        toast.success(t('verification.email_sent') || 'Email de verificación enviado. Revisa tu bandeja de entrada.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('verification.email_error') || 'Error enviando email');
    } finally {
      setVerifying(false);
    }
  };

  const openPhoneVerifyModal = () => {
    setPhoneVerifyForm({
      phone: user?.phone || '',
      code: '',
      step: 'phone',
      codeSent: false,
      mockMode: false
    });
    setShowPhoneVerifyModal(true);
  };

  const sendPhoneCode = async () => {
    if (!phoneVerifyForm.phone) {
      toast.error(t('verification.phone_required') || 'Introduce tu número de móvil');
      return;
    }
    
    setVerifying(true);
    try {
      const response = await axios.post(`${API}/verification/send-phone-code`, {
        phone: phoneVerifyForm.phone
      });
      
      if (response.data.mock) {
        toast.success(response.data.message || t('verification.phone_mock') || 'Modo prueba: usa código 123456');
        setPhoneVerifyForm(prev => ({ ...prev, step: 'code', codeSent: true, mockMode: true }));
      } else {
        toast.success(t('verification.code_sent') || 'Código enviado por SMS');
        setPhoneVerifyForm(prev => ({ ...prev, step: 'code', codeSent: true, mockMode: false }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('verification.phone_error') || 'Error enviando código');
    } finally {
      setVerifying(false);
    }
  };

  const verifyPhoneCode = async () => {
    if (!phoneVerifyForm.code || phoneVerifyForm.code.length < 6) {
      toast.error(t('verification.code_invalid') || 'Introduce el código de 6 dígitos');
      return;
    }
    
    setVerifying(true);
    try {
      const response = await axios.post(`${API}/verification/verify-phone`, {
        phone: phoneVerifyForm.phone,
        code: phoneVerifyForm.code
      }, {
        headers: { 'X-User-Id': user.id }
      });
      
      if (response.data.valid) {
        toast.success(t('verification.phone_verified') || '¡Móvil verificado correctamente!');
        // Update local user state
        const updatedUser = { ...user, phoneVerified: true, phone: phoneVerifyForm.phone };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setShowPhoneVerifyModal(false);
        // Refresh profile from server
        fetchProfile();
      } else {
        toast.error(t('verification.code_wrong') || 'Código incorrecto. Inténtalo de nuevo.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('verification.verify_error') || 'Error verificando código');
    } finally {
      setVerifying(false);
    }
  };

  const handleCancelReservation = async (reservationId) => {
    if (!window.confirm(t('reservation.cancel_confirm') || '¿Cancelar esta reserva?')) return;
    
    try {
      await axios.delete(`${API}/reservations/${reservationId}`, {
        headers: { 'X-User-Id': user.id }
      });
      toast.success(t('reservation.cancelled') || 'Reserva cancelada');
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error cancelando reserva');
    }
  };

  const handleUploadTicket = (reservationId) => {
    // Find the reservation
    const reservation = reservations.find(r => r.id === reservationId);
    if (reservation) {
      setTicketUploadReservation(reservation);
    }
  };

  const handleVerificationComplete = (result) => {
    fetchReservations();
    fetchProfile(); // Refresh points
    setTicketUploadReservation(null);
  };

  // Count active reservations
  const activeReservations = reservations.filter(r => 
    ['pending', 'verification_pending'].includes(r.status)
  ).length;

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-local-sage to-local-sage/80 rounded-3xl p-6 sm:p-8 text-white mb-8 relative overflow-hidden"
        >
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl" />
          </div>
          
          <div className="relative z-10">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/20 flex items-center justify-center">
                  <UserIcon className="w-7 h-7 sm:w-8 sm:h-8" />
                </div>
                <div>
                  <h1 className="font-manrope font-bold text-2xl sm:text-3xl">
                    {user?.name} {user?.surname}
                  </h1>
                  <p className="text-white/80 text-sm sm:text-base">{user?.email}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setEditMode(!editMode)}
                  className="bg-white/20 hover:bg-white/30 text-white border-0"
                >
                  <Edit3 className="w-4 h-4 mr-1" />
                  Editar
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowSupportModal(true)}
                  className="bg-white/20 hover:bg-white/30 text-white border-0"
                >
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Soporte
                </Button>
              </div>
            </div>

            {/* Verification Status */}
            <div className="flex flex-wrap gap-2 mb-4">
              <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs ${user?.phoneVerified ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
                {user?.phoneVerified ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                <span>{t('profile.phone') || 'Móvil'} {user?.phoneVerified ? (t('profile.verified') || 'verificado') : (t('profile.not_verified') || 'no verificado')}</span>
                {!user?.phoneVerified && (
                  <button 
                    onClick={openPhoneVerifyModal} 
                    className="ml-1 underline text-xs font-medium hover:text-white/90"
                    data-testid="verify-phone-btn"
                  >
                    {t('profile.verify') || 'Verificar'}
                  </button>
                )}
              </div>
              <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs ${user?.emailVerified ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
                {user?.emailVerified ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                <span>Email {user?.emailVerified ? (t('profile.verified') || 'verificado') : (t('profile.not_verified') || 'no verificado')}</span>
                {!user?.emailVerified && (
                  <button 
                    onClick={sendEmailVerification} 
                    disabled={verifying}
                    className="ml-1 underline text-xs font-medium hover:text-white/90 disabled:opacity-50"
                    data-testid="verify-email-btn"
                  >
                    {verifying ? <Loader2 className="w-3 h-3 animate-spin inline" /> : (t('profile.send_verification') || 'Enviar')}
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                <div className="flex items-center gap-1 sm:gap-2 mb-1">
                  <Gift className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-xs sm:text-sm text-white/80">Puntos</span>
                </div>
                <p className="text-2xl sm:text-3xl font-bold" data-testid="user-points">{user?.points || 0}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                <div className="flex items-center gap-1 sm:gap-2 mb-1">
                  <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-xs sm:text-sm text-white/80">Kompras</span>
                </div>
                <p className="text-2xl sm:text-3xl font-bold">{purchases.length}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                <div className="flex items-center gap-1 sm:gap-2 mb-1">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-xs sm:text-sm text-white/80">Miembro</span>
                </div>
                <p className="text-sm sm:text-lg font-semibold">
                  {new Date(user?.createdAt).toLocaleDateString('es-ES', { month: 'short', year: 'numeric' })}
                </p>
              </div>
            </div>

            {/* User Counter - Community Progress */}
            <div className="mt-4">
              <UserCounter variant="progress" />
            </div>
            
            {/* Sponsored Content - Minimal inline ad */}
            <div className="mt-4">
              <InlineAdBanner />
            </div>
          </div>
        </motion.div>

        {/* Edit Profile Form */}
        <AnimatePresence>
          {editMode && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-card rounded-2xl p-6 shadow-card border border-border/50 mb-8"
            >
              <h3 className="font-semibold text-lg mb-4">Editar Perfil</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Nombre</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Apellidos</Label>
                  <Input
                    value={editForm.surname}
                    onChange={(e) => setEditForm({...editForm, surname: e.target.value})}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                      className="pl-10 rounded-xl"
                    />
                  </div>
                </div>
                <div>
                  <Label>Móvil</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="tel"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                      className="pl-10 rounded-xl"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <Button onClick={handleSaveProfile} disabled={saving} className="bg-local-sage hover:bg-local-sage/90">
                  {saving ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
                <Button variant="outline" onClick={() => setShowPasswordModal(true)}>
                  <Lock className="w-4 h-4 mr-2" />
                  Cambiar Contraseña
                </Button>
                <Button variant="ghost" onClick={() => setEditMode(false)}>Cancelar</Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Notification Settings */}
        <NotificationSettings userId={user?.id} />

        {/* Tab Navigation */}
        <div className="flex border-b border-border mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('reservations')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'reservations'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="reservations-tab"
          >
            <CalendarCheck className="w-4 h-4" />
            {t('profile.reservations') || 'Mis Reservas'}
            {activeReservations > 0 && (
              <span className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200 text-xs px-2 py-0.5 rounded-full">
                {activeReservations}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('progress')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'progress'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="progress-tab"
          >
            <Trophy className="w-4 h-4" />
            {t('gamification.title') || 'Tu Progreso'}
          </button>
          <button
            onClick={() => setActiveTab('referrals')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'referrals'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="referrals-tab"
          >
            <Users className="w-4 h-4" />
            {t('referrals.title') || 'Referidos'}
          </button>
          <button
            onClick={() => setActiveTab('purchases')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'purchases'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="purchases-tab"
          >
            <ShoppingBag className="w-4 h-4" />
            {t('profile.purchases') || 'Historial'}
          </button>
          <button
            onClick={() => setActiveTab('privacy')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'privacy'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="privacy-tab"
          >
            <Shield className="w-4 h-4" />
            {t('gdpr.tab_title') || 'Privacidad'}
          </button>
        </div>

        {/* Reservations Section */}
        {activeTab === 'reservations' && (
          <div className="mb-8">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
              </div>
            ) : reservations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="reservations-list">
                {reservations.map((reservation) => (
                  <ReservationCard
                    key={reservation.id}
                    reservation={reservation}
                    onUploadTicket={handleUploadTicket}
                    onCancel={handleCancelReservation}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-2xl">
                <CalendarCheck className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  {t('profile.no_reservations') || 'No tienes reservas activas'}
                </p>
                <Button
                  onClick={() => window.location.href = '/kupons'}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {t('profile.browse_offers') || 'Explorar ofertas'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Gamification/Progress Section */}
        {activeTab === 'progress' && (
          <div className="mb-8">
            <GamificationPanel userId={user?.id} language={i18n.language} />
          </div>
        )}

        {/* Referrals Section */}
        {activeTab === 'referrals' && (
          <div className="mb-8">
            <ReferralSection userId={user?.id} />
          </div>
        )}

        {/* Purchases History */}
        {activeTab === 'purchases' && (
        <div>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-local-sage" />
            </div>
          ) : purchases.length > 0 ? (
            <div className="space-y-4" data-testid="purchases-list">
              {purchases.map((purchase) => (
                <motion.div
                  key={purchase.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-card rounded-2xl p-4 sm:p-6 shadow-card border border-border/50"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-base sm:text-lg mb-2">{purchase.kuponTitle}</h3>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
                        <span>{new Date(purchase.createdAt).toLocaleDateString('es-ES')}</span>
                        <span className="hidden sm:inline">•</span>
                        <span>{purchase.amount.toFixed(2)}€</span>
                        <span className="hidden sm:inline">•</span>
                        <span className="text-local-sage font-medium">+{purchase.points} puntos</span>
                      </div>
                    </div>
                    <div>
                      <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                        purchase.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' :
                        purchase.status === 'ready' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100' :
                        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100'
                      }`}>
                        {purchase.status === 'completed' ? 'Kompletado' :
                         purchase.status === 'ready' ? 'Listo' :
                         'Pendiente'}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ShoppingBag className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aún no has realizado ninguna kompra</p>
            </div>
          )}
        </div>
        )}

        {/* Privacy/Data Management Section */}
        {activeTab === 'privacy' && (
          <div className="mb-8">
            <DataPrivacySection userId={user?.id} userType="customer" />
          </div>
        )}
      </div>

      {/* Password Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPasswordModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card rounded-2xl p-6 w-full max-w-md shadow-xl"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Cambiar Contraseña</h3>
                <button onClick={() => setShowPasswordModal(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Contraseña Actual</Label>
                  <Input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Nueva Contraseña</Label>
                  <Input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Confirmar Nueva Contraseña</Label>
                  <Input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                    className="rounded-xl"
                  />
                </div>
                <Button onClick={handleChangePassword} disabled={saving} className="w-full bg-local-sage hover:bg-local-sage/90">
                  {saving ? 'Guardando...' : 'Cambiar Contraseña'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Support Modal */}
      <AnimatePresence>
        {showSupportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => setShowSupportModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card rounded-2xl p-6 w-full max-w-md shadow-xl"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Contactar Soporte</h3>
                <button onClick={() => setShowSupportModal(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Asunto</Label>
                  <Input
                    value={supportForm.subject}
                    onChange={(e) => setSupportForm({...supportForm, subject: e.target.value})}
                    placeholder="¿En qué podemos ayudarte?"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Mensaje</Label>
                  <textarea
                    value={supportForm.message}
                    onChange={(e) => setSupportForm({...supportForm, message: e.target.value})}
                    placeholder="Describe tu consulta o problema..."
                    className="w-full min-h-[120px] px-3 py-2 bg-background border border-input rounded-xl resize-none"
                  />
                </div>
                <Button onClick={handleSendSupport} disabled={saving} className="w-full bg-local-sage hover:bg-local-sage/90">
                  <Send className="w-4 h-4 mr-2" />
                  {saving ? 'Enviando...' : 'Enviar Mensaje'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Phone Verification Modal */}
      <AnimatePresence>
        {showPhoneVerifyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPhoneVerifyModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card rounded-2xl p-6 w-full max-w-md shadow-xl"
              data-testid="phone-verify-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Phone className="w-5 h-5 text-local-sage" />
                  {t('verification.verify_phone') || 'Verificar Móvil'}
                </h3>
                <button onClick={() => setShowPhoneVerifyModal(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {phoneVerifyForm.step === 'phone' ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {t('verification.phone_instruction') || 'Introduce tu número de móvil para recibir un código de verificación por SMS.'}
                  </p>
                  
                  {/* Important notice about phone prefix */}
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-800 dark:text-amber-200">
                          {t('verification.prefix_required') || 'Incluye el prefijo internacional'}
                        </p>
                        <p className="text-amber-700 dark:text-amber-300 text-xs mt-1">
                          {t('verification.prefix_example') || 'Ejemplo: +34 para España, +49 para Alemania, +33 para Francia'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label>{t('profile.phone') || 'Número de Móvil'}</Label>
                    <Input
                      type="tel"
                      value={phoneVerifyForm.phone}
                      onChange={(e) => setPhoneVerifyForm({...phoneVerifyForm, phone: e.target.value})}
                      placeholder="+34 612 345 678"
                      className="rounded-xl"
                      data-testid="phone-input"
                    />
                  </div>
                  <Button 
                    onClick={sendPhoneCode} 
                    disabled={verifying || !phoneVerifyForm.phone} 
                    className="w-full bg-local-sage hover:bg-local-sage/90"
                    data-testid="send-code-btn"
                  >
                    {verifying ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('verification.sending') || 'Enviando...'}</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> {t('verification.send_code') || 'Enviar Código'}</>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div className="text-sm">
                      <p className="font-medium text-green-800 dark:text-green-200">
                        {t('verification.code_sent_to') || 'Código enviado a'} {phoneVerifyForm.phone}
                      </p>
                      {phoneVerifyForm.mockMode && (
                        <p className="text-green-600 dark:text-green-400 text-xs">
                          {t('verification.mock_hint') || 'Modo prueba: usa código 123456'}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <Label>{t('verification.enter_code') || 'Código de Verificación'}</Label>
                    <Input
                      type="text"
                      value={phoneVerifyForm.code}
                      onChange={(e) => setPhoneVerifyForm({...phoneVerifyForm, code: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      placeholder="123456"
                      maxLength={6}
                      className="rounded-xl text-center text-2xl tracking-widest font-mono"
                      data-testid="code-input"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={() => setPhoneVerifyForm(prev => ({ ...prev, step: 'phone', code: '' }))}
                      className="flex-1"
                    >
                      {t('back') || 'Atrás'}
                    </Button>
                    <Button 
                      onClick={verifyPhoneCode} 
                      disabled={verifying || phoneVerifyForm.code.length < 6} 
                      className="flex-1 bg-local-sage hover:bg-local-sage/90"
                      data-testid="verify-code-btn"
                    >
                      {verifying ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('verification.verifying') || 'Verificando...'}</>
                      ) : (
                        <><CheckCircle className="w-4 h-4 mr-2" /> {t('verification.verify') || 'Verificar'}</>
                      )}
                    </Button>
                  </div>
                  
                  <button 
                    onClick={sendPhoneCode}
                    disabled={verifying}
                    className="text-sm text-local-sage hover:underline w-full text-center"
                  >
                    {t('verification.resend_code') || '¿No recibiste el código? Reenviar'}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ticket Upload Modal */}
      <AnimatePresence>
        {ticketUploadReservation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setTicketUploadReservation(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <TicketUploader
                reservationId={ticketUploadReservation.id}
                userId={user?.id}
                onVerificationComplete={handleVerificationComplete}
                onClose={() => setTicketUploadReservation(null)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
